/**
 * Ask AI panel ("Ask about this page"), shown in the help panel.
 *
 * Opens with one-click chips (quick actions first, then the open tab's and the page's suggested
 * questions). Every request sends the page, the open tab and the live route. Answers are sanitised
 * HTML; under each answer: "Go to {page}" (stays open), "You are here", "Show me how" (opens the page
 * and starts its tour) and, under the newest answer, follow-up chips. Keeps the last 5 questions
 * with "Start over". Enter sends, Shift+Enter adds a line.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconArrowRight, IconMapPin, IconRefresh, IconRoute, IconSend, IconSparkles } from '@tabler/icons-react';
import { Alert, Badge, Button, Group, Loader, Stack, Text, Textarea } from '@/components/ui';
import type { HelpPageView, HelpTabView } from '@actschurch/ccms-sdk';
import { useHelpContext, type HelpAskEntry } from './HelpContext';
import { HelpHtml } from './HelpHtml';
import { quickActions, suggestedChips, type AskChip } from './askChips';
import classes from './help.module.css';

const SHOWN_SUGGESTIONS = 6;

interface AskAiPanelProps {
  pageKey: string;
  page: HelpPageView | null;
  tab: HelpTabView | undefined;
  tabKey: string | null;
  focusNonce?: number;
}

function ChipButton({ chip, disabled, onPick }: { chip: AskChip; disabled: boolean; onPick: (chip: AskChip) => void }) {
  return (
    <Button
      size="compact-sm"
      variant="light"
      radius="xl"
      disabled={disabled}
      onClick={() => onPick(chip)}
      styles={{ root: { height: 'auto', minHeight: 26, paddingTop: 3, paddingBottom: 3 }, label: { whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.3 } }}
    >
      {chip.label}
    </Button>
  );
}

export function AskAiPanel({ pageKey, page, tab, tabKey, focusNonce }: AskAiPanelProps) {
  const ctx = useHelpContext();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const conversation = ctx?.conversation ?? [];
  const busy = conversation.some((e) => e.status === 'loading');
  const lastId = conversation.length ? conversation[conversation.length - 1].id : null;

  useEffect(() => {
    if (focusNonce) inputRef.current?.focus();
  }, [focusNonce]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [conversation]);

  if (!ctx) return null;

  const quick = quickActions(!!tabKey);
  const suggestions = suggestedChips(page, tab);
  const shown = showAll ? suggestions : suggestions.slice(0, SHOWN_SUGGESTIONS);

  const send = (chip: AskChip) => {
    if (busy) return;
    ctx.askAi({ label: chip.label, question: chip.question, intent: chip.intent, pageKey, tabKey });
  };

  const sendText = () => {
    const q = text.trim();
    if (!q || busy) return;
    ctx.askAi({ label: q, question: q, pageKey, tabKey });
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendText();
    }
  };

  return (
    <Stack gap="sm">
      {conversation.length === 0 && (
        <Text size="sm" c="dimmed">
          Pick a question or type your own. Answers come from the help for this app.
        </Text>
      )}

      {conversation.length > 0 && (
        <Stack gap="sm" aria-live="polite">
          {conversation.map((entry) => (
            <AnswerBlock
              key={entry.id}
              entry={entry}
              latest={entry.id === lastId}
              busy={busy}
              onPick={send}
              onGo={(route) => navigate(route)}
              onShowMe={(route, tourKey, linkPageKey) => ctx.showMeHow(route, tourKey, linkPageKey)}
            />
          ))}
          <div ref={endRef} />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" size="compact-xs" leftSection={<IconRefresh size={12} />} onClick={ctx.clearConversation} disabled={busy}>
              Start over
            </Button>
          </Group>
        </Stack>
      )}

      <Stack gap={6}>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Quick questions
        </Text>
        <Group gap={6}>
          {quick.map((chip) => (
            <ChipButton key={chip.label} chip={chip} disabled={busy} onPick={send} />
          ))}
        </Group>
        {suggestions.length > 0 && (
          <>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mt={4}>
              Suggested
            </Text>
            <Group gap={6}>
              {shown.map((chip) => (
                <ChipButton key={chip.label} chip={chip} disabled={busy} onPick={send} />
              ))}
              {suggestions.length > SHOWN_SUGGESTIONS && (
                <Button size="compact-sm" variant="subtle" radius="xl" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Less' : `More (${suggestions.length - SHOWN_SUGGESTIONS})`}
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>

      <Stack gap={6}>
        <Textarea
          ref={inputRef}
          aria-label="Your question"
          placeholder="Or type a question about this page"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          autosize
          minRows={2}
          maxRows={5}
          maxLength={500}
          disabled={busy}
        />
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed">
            Enter sends. Shift and Enter adds a line.
          </Text>
          <Button size="compact-sm" leftSection={<IconSend size={14} />} onClick={sendText} loading={busy} disabled={!text.trim()}>
            Ask
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
}

function AnswerBlock({
  entry,
  latest,
  busy,
  onPick,
  onGo,
  onShowMe,
}: {
  entry: HelpAskEntry;
  latest: boolean;
  busy: boolean;
  onPick: (chip: AskChip) => void;
  onGo: (route: string) => void;
  onShowMe: (route: string, tourKey: string, pageKey: string) => void;
}) {
  const result = entry.result;
  const links = result?.links ?? [];
  const followUps = (result?.followUps ?? []).filter((f) => !!f && f.trim().length > 0);
  const answered = !!result && result.enabled && !!result.html;

  return (
    <Stack gap={6}>
      <div className={classes.question}>{entry.label}</div>
      <div className={classes.answer}>
        {entry.status === 'loading' && (
          <Group gap="xs" wrap="nowrap">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Finding the answer
            </Text>
          </Group>
        )}
        {entry.status === 'error' && (
          <Alert color="orange" alertVariant="light" p="xs">
            <Text size="sm">{entry.error}</Text>
          </Alert>
        )}
        {entry.status === 'done' && result && (
          <Stack gap="sm">
            {answered ? (
              <HelpHtml html={result.html} />
            ) : (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <IconSparkles size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <Text size="sm">{result.message || 'AI help could not answer that. Try the help for this page.'}</Text>
              </Group>
            )}
            {answered && result.message && (
              <Text size="xs" c="dimmed">
                {result.message}
              </Text>
            )}
            {links.length > 0 && (
              <Stack gap={6}>
                {links.map((link) => (
                  <Group key={`${link.pageKey}-${link.route}`} gap={6} wrap="wrap">
                    {link.isCurrentPage ? (
                      <Badge size="lg" variant="light" color="teal" leftSection={<IconMapPin size={12} />} styles={{ root: { textTransform: 'none' } }}>
                        You are here: {link.title}
                      </Badge>
                    ) : (
                      <Button size="compact-sm" variant="default" rightSection={<IconArrowRight size={14} />} onClick={() => onGo(link.route)} title={link.menuPath ?? undefined}>
                        Go to {link.title}
                      </Button>
                    )}
                    {link.tourKey && (
                      <Button size="compact-sm" variant="light" leftSection={<IconRoute size={14} />} onClick={() => onShowMe(link.route, link.tourKey as string, link.pageKey)}>
                        Show me how
                      </Button>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
            {latest && followUps.length > 0 && (
              <Group gap={6}>
                {followUps.map((f) => (
                  <ChipButton key={f} chip={{ label: f, question: f }} disabled={busy} onPick={onPick} />
                ))}
              </Group>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  );
}
